import type { ApolloError } from "@apollo/client"
import { useAtom } from "jotai"
import React, { useEffect, useState } from "react"
import { userDetailsAtom } from "../../../components/Menu/menu.store"
import { useViewUserArchivedFmPsLazyQuery, useViewUserFmPsLazyQuery, type ViewUserFmPsQuery } from "../../../type-and-hooks"
import { formData } from "./formData.store"

type FilteredDataType = ViewUserFmPsQuery["viewUserFMPs"]
const useFetchFilteredFMP = (
  fmpId: number | undefined
): {
  filteredFMP: FilteredDataType
  error: ApolloError | undefined
  loading: boolean
  refetch: VoidFunction
} => {
  // Return the filtered FMP data, error and loading state
  const [filteredFMP, setFilteredFMP] = React.useState<FilteredDataType>([])
  const [error, setError] = React.useState<ApolloError | undefined>()
  const [refetchNumber, setRefetchNumber] = useState<number>(0)
  const hasFetchedRef = React.useRef<boolean>(false)
  const hasFetchedArchivedRef = React.useRef<boolean>(false)
  const [fetchFMPs, { data: viewFMPData, loading }] = useViewUserFmPsLazyQuery({
    fetchPolicy: "no-cache"
  })
  const [fetchArchivedFMP, { data: archivedFMPData, loading: archivedFMPsLoading, error: viewArchivedFMPError }] = useViewUserArchivedFmPsLazyQuery({
    fetchPolicy: "no-cache"
  })
  const [, setFormDataAtom] = useAtom(formData)
  const [userDetails] = useAtom(userDetailsAtom)

  const handleError = (error: ApolloError): void => {
    setError(error)
  }

  // Reset hasFetched when refetch is triggered - must run before the fetch
  // effect below (React commits effects in declaration order) so a refetch
  // actually fires on the same commit instead of arming the next one.
  useEffect(() => {
    if (refetchNumber > 0) {
      hasFetchedRef.current = false
      hasFetchedArchivedRef.current = false
    }
  }, [refetchNumber])

  useEffect(() => {
    if (userDetails?._id && !hasFetchedRef.current && !loading) {
      hasFetchedRef.current = true
      fetchFMPs({
        variables: { userId: userDetails?._id },
        fetchPolicy: "no-cache"
      }).catch(handleError)
    }
  }, [fetchFMPs, userDetails, refetchNumber, loading])

  // Reset flags when fmpId changes to allow fetching new FMP data
  useEffect(() => {
    hasFetchedRef.current = false
    hasFetchedArchivedRef.current = false
  }, [fmpId])
  const setFMP = (filteredFMP: FilteredDataType): void => {
    setFilteredFMP(filteredFMP)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { project_name, project_address, subcontractor_address, subcontractor_name, revision, date_created, date_modified, subcontractor_logo } =
      filteredFMP?.[0] ?? {
        project_name: "",
        project_address: "",
        company_name: "",
        company_address: "",
        subcontractor_logo: ""
      }

    setFormDataAtom((prevState: any) => ({
      ...prevState,
      projectName: project_name,
      projectAddress: project_address,
      companyName: subcontractor_name,
      companyAddress: subcontractor_address,
      companyLogo: subcontractor_logo,
      dateCreated: date_created,
      dateModified: date_modified,
      revision
    }))
  }

  useEffect(() => {
    const filteredFMP = viewFMPData?.viewUserFMPs?.filter(fmp => fmp?.id === fmpId) ?? null

    if (!filteredFMP?.length) {
      if (userDetails?._id && !hasFetchedArchivedRef.current && !archivedFMPsLoading) {
        hasFetchedArchivedRef.current = true
        fetchArchivedFMP({
          variables: { userId: userDetails?._id },
          fetchPolicy: "no-cache"
        }).catch(handleError)
      }
    }

    if (filteredFMP?.length) {
      setFMP(filteredFMP)
    }
  }, [viewFMPData, fmpId, userDetails?._id, archivedFMPsLoading, fetchArchivedFMP])
  useEffect(() => {
    if (viewArchivedFMPError) {
      handleError(viewArchivedFMPError)
    }

    if (archivedFMPData) {
      const filteredArchivedFMP = archivedFMPData?.viewUserArchivedFMPs?.filter(fmp => fmp?.id === fmpId) ?? null
      setFilteredFMP(filteredArchivedFMP)
    }
  }, [archivedFMPData, viewArchivedFMPError])

  return {
    filteredFMP,
    error,
    loading: loading || archivedFMPsLoading,
    refetch: () => {
      setRefetchNumber(() => refetchNumber + 1)
    }
  }
}

export default useFetchFilteredFMP
